import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { addVaultGoogleFile, linkVaultFolder, relinkVaultFolder, syncVaultFolder, unlinkVaultFolder } from '@/data/client';
import type { VaultFolder } from '@/data/types';
import { ConnectGoogleButton, useGoogleStatus } from '@/components/crm/GoogleConnect';
import { FormError, Modal } from '@/components/crm/Modal';

/* Google Drive for one company's Vault: link the client's folder at setup
   (subfolders included, kept in sync every 30 minutes), or add single Docs,
   Sheets and Slides. Uses the linking person's own Drive access. */
export function DrivePanel({ companyId, folders }: { companyId: string; folders: VaultFolder[] }) {
  const qc = useQueryClient();
  const { data: google } = useGoogleStatus();
  const [folderUrl, setFolderUrl] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [unlinking, setUnlinking] = useState<VaultFolder | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['vault', companyId] });
  const link = useMutation({ mutationFn: () => linkVaultFolder(companyId, folderUrl.trim()), onSuccess: () => { setFolderUrl(''); refresh(); } });
  const addFile = useMutation({ mutationFn: () => addVaultGoogleFile(companyId, fileUrl.trim()), onSuccess: () => { setFileUrl(''); refresh(); } });
  const sync = useMutation({ mutationFn: (v: { id: string; force?: boolean }) => syncVaultFolder(v.id, v.force), onSuccess: refresh });
  const relink = useMutation({ mutationFn: relinkVaultFolder, onSuccess: refresh });
  const unlink = useMutation({ mutationFn: unlinkVaultFolder, onSuccess: () => { setUnlinking(null); refresh(); } });
  const canLink = !!google?.connected && google.driveGranted;

  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3><Icon name="layers" size={15} /> Google Drive</h3></div>

      {folders.length > 0 && (
        <ul className="crm-people" style={{ marginBottom: 14 }}>
          {folders.map((f) => (
            <li key={f.id}>
              <div>
                <a href={`https://drive.google.com/drive/folders/${f.driveFolderId}`} target="_blank" rel="noreferrer">{f.name ?? 'Drive folder'}</a>
                <span className="crm-cell-sub">
                  {f.files} document{f.files === 1 ? '' : 's'}{f.skippedFiles ? ` · ${f.skippedFiles} images, media and design files skipped` : ''} · {f.lastSyncedAt ? `synced ${relativeTime(f.lastSyncedAt)}` : 'first sync in progress…'} · via {f.linkedByMe ? 'your Google account' : f.linkedBy ?? 'a former member'}
                </span>
                {f.syncError && <span className="crm-error" style={{ display: 'block', marginTop: 6 }}>{f.syncError}</span>}
              </div>
              <span className="crm-record__actions">
                {(f.syncError || !f.linkedByMe) && canLink && (
                  <Button variant="outline" size="sm" onClick={() => relink.mutate(f.id)} disabled={relink.isPending} title="Sync this folder with your Google access from now on">Use my access</Button>
                )}
                <Button variant="ghost" size="sm" iconLeft={<Icon name="refresh-cw" size={13} />} onClick={() => sync.mutate({ id: f.id })} disabled={sync.isPending}>Sync now</Button>
                <Button variant="ghost" size="sm" onClick={() => sync.mutate({ id: f.id, force: true })} disabled={sync.isPending} title="Read every document in this folder again">Re-read all</Button>
                <button type="button" className="lead-modal__x" aria-label={`Unlink ${f.name}`} title="Unlink folder" onClick={() => setUnlinking(f)}><Icon name="x" size={14} /></button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {!google?.configured ? <p className="crm-cell-muted">Google isn't set up on this server.</p>
        : !canLink ? (
          <div className="crm-form">
            <p className="crm-cell-muted">
              {google.connected
                ? 'Your Google connection doesn’t include Drive yet. Reconnect and leave the Drive box ticked; Calendar keeps working.'
                : 'Connect your Triton Google account to link this client’s Drive folder.'}
            </p>
            <div><ConnectGoogleButton label={google.connected ? 'Allow Google Drive access' : 'Connect Google'} /></div>
          </div>
        ) : (
          <div className="crm-form">
            <form className="crm-addopt" onSubmit={(e) => { e.preventDefault(); if (folderUrl.trim()) link.mutate(); }}>
              <Input label="Link the client's Drive folder" placeholder="https://drive.google.com/drive/folders/…" value={folderUrl} onChange={(e) => setFolderUrl(e.target.value)} />
              <Button type="submit" size="sm" disabled={!folderUrl.trim() || link.isPending}>{link.isPending ? 'Linking…' : 'Link folder'}</Button>
            </form>
            <form className="crm-addopt" onSubmit={(e) => { e.preventDefault(); if (fileUrl.trim()) addFile.mutate(); }}>
              <Input label="Or add one Google Doc, Sheet or Slides" placeholder="https://docs.google.com/…" value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} />
              <Button type="submit" size="sm" variant="outline" disabled={!fileUrl.trim() || addFile.isPending}>Add</Button>
            </form>
            <p className="crm-cell-sub">Every document in the folder and its subfolders is read. Images, video, archives and design files are skipped. Changes in Drive reach the Vault within 30 minutes.</p>
          </div>
        )}
      <FormError error={link.error ?? addFile.error ?? sync.error ?? relink.error} />

      {unlinking && (
        <Modal title={`Unlink “${unlinking.name}”?`} onClose={() => setUnlinking(null)} footer={<>
          <Button variant="ghost" onClick={() => setUnlinking(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => unlink.mutate(unlinking.id)} disabled={unlink.isPending}>Unlink and remove {unlinking.files} documents</Button>
        </>}>
          <p>Its {unlinking.files} documents leave the Client Vault. Nothing changes in Google Drive, and you can link the folder again later.</p>
          <FormError error={unlink.error} />
        </Modal>
      )}
    </Card>
  );
}
